import { useEffect, useRef, useState } from "react"
import { PermissionsAndroid, Platform } from "~/ui/primitives"
import {
  AudioEncoderAndroidType,
  AudioSet,
  AudioSourceAndroidType,
  AVEncoderAudioQualityIOSType,
  AVEncodingOption,
  RecordBackType,
} from "~/ui/audio-recorder"
import { useAudioRecorder } from "../context"
import { debug, DocumentDir, newID, globalToast } from "../utils"

/** 录音时长上限（与 native 回调 `currentPosition` 一致，单位为毫秒） */
const MAX_RECORD_MS = 60 * 1000

// 语音输入
const useVoiceRecord = ({ onVoiceRecorded }) => {
  const audioRecorderPlayer = useAudioRecorder()
  const recordingActiveRef = useRef(false)
  const startAtRef = useRef<number | null>(null)
  const fallbackTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const lastPosRef = useRef<number>(0)
  /** 已达时长上限正在收尾，忽略松手触发的重复 stop */
  const autoFinalizeRef = useRef(false)
  const [recording, setRecording] = useState<boolean>(false)
  const [recordSecs, setRecordSecs] = useState<number>(0)
  const [recordTime, setRecordTime] = useState<string>("00:00")
  const [isCancelStyle, setIsCancelStyle] = useState(false)

  const clearFallbackTimer = () => {
    if (fallbackTimerRef.current) {
      clearInterval(fallbackTimerRef.current)
      fallbackTimerRef.current = null
    }
  }

  // 开始录音
  const onVoiceRecorderStart = async () => {
    // H5：浏览器麦克风权限由 getUserMedia 触发
    if (Platform.OS === "web") {
      if (!navigator.mediaDevices?.getUserMedia) {
        globalToast.show("当前浏览器不支持录音")
        return
      }
    } else {
      const OsVer = Platform.constants.Release
      debug("OsVer", OsVer)
      if (Platform.OS === "android") {
        try {
          const recordRes = await PermissionsAndroid.request(
            PermissionsAndroid.PERMISSIONS.RECORD_AUDIO,
          )
          if (recordRes !== PermissionsAndroid.RESULTS.GRANTED) {
            globalToast.show("需要开启录音权限")
            return
          }

          if (parseInt(String(OsVer || "0"), 10) < 13) {
            await PermissionsAndroid.requestMultiple([
              PermissionsAndroid.PERMISSIONS.WRITE_EXTERNAL_STORAGE,
              PermissionsAndroid.PERMISSIONS.READ_EXTERNAL_STORAGE,
            ])
          }
        } catch (err) {
          globalToast.show("需要开启录音权限")
          return
        }
      }
    }

    const audioSet: AudioSet = {
      AudioEncoderAndroid: AudioEncoderAndroidType.AAC,
      AudioSourceAndroid: AudioSourceAndroidType.MIC,
      AVEncoderAudioQualityKeyIOS: AVEncoderAudioQualityIOSType.high,
      AVNumberOfChannelsKeyIOS: 2,
      AVFormatIDKeyIOS: AVEncodingOption.aac,
    }

    const filename = newID()
    const path = Platform.select({
      web: `${filename}.webm`,
      ios: `${filename}.m4a`,
      android: `${DocumentDir}/${filename}.m4a`,
      default: `${filename}.webm`,
    })

    // use default path
    const uri = await audioRecorderPlayer.startRecorder(path, audioSet)
    debug(`audio file: ${uri}`)

    // 兜底计时：部分鸿蒙机型 currentPosition 一直是 0 或回调不稳定
    startAtRef.current = Date.now()
    lastPosRef.current = 0
    clearFallbackTimer()
    fallbackTimerRef.current = setInterval(() => {
      if (!recordingActiveRef.current) return
      // 若 native 回调在走，就不需要兜底（lastPosRef 会被更新）
      if (lastPosRef.current > 0) return
      const startAt = startAtRef.current
      if (!startAt) return
      const ms = Math.min(Date.now() - startAt, MAX_RECORD_MS)
      setRecordSecs(ms)
      setRecordTime(audioRecorderPlayer.mmss(Math.floor(ms / 1000)))
      if (ms >= MAX_RECORD_MS && recordingActiveRef.current) {
        // 超时自动收尾逻辑交给 native listener/stop
      }
    }, 200)

    audioRecorderPlayer.addRecordBackListener((e: RecordBackType) => {
      const sec = Math.floor(e.currentPosition)
      lastPosRef.current = sec
      setRecordSecs(sec)
      setRecordTime(audioRecorderPlayer.mmss(Math.floor(sec / 1000)))

      if (sec >= MAX_RECORD_MS && recordingActiveRef.current) {
        recordingActiveRef.current = false
        autoFinalizeRef.current = true
        audioRecorderPlayer.removeRecordBackListener()
        clearFallbackTimer()
        void (async () => {
          try {
            const path = await audioRecorderPlayer.stopRecorder()
            const duration = Math.min(sec, MAX_RECORD_MS)
            if (duration >= 1500) {
              onVoiceRecorded({ duration, path })
            }
          } catch {
            //
          } finally {
            autoFinalizeRef.current = false
            reset()
          }
        })()
      }
    })
    recordingActiveRef.current = true
    setRecording(true)
  }

  const reset = () => {
    recordingActiveRef.current = false
    startAtRef.current = null
    lastPosRef.current = 0
    clearFallbackTimer()
    setRecordSecs(0)
    setRecordTime("00:00")
    setRecording(false)
    setIsCancelStyle(false)
  }

  // 停止录音
  const onVoiceRecorderStop = async (isCancel = false) => {
    if (autoFinalizeRef.current) {
      return
    }
    if (!recording) {
      return
    }

    // 取消錄音
    if (isCancelStyle) {
      audioRecorderPlayer.stopRecorder()
      audioRecorderPlayer.removeRecordBackListener()
      reset()
      return
    }

    try {
      const path = await audioRecorderPlayer.stopRecorder()
      audioRecorderPlayer.removeRecordBackListener()

      if (isCancel) {
        return
      }
      if (recordSecs < 1500) {
        globalToast.show("说话时间太短")
        return
      }
      onVoiceRecorded({ duration: recordSecs, path })
    } catch (e) {
      // console.log(e, 'e---')
    } finally {
      reset()
    }
  }

  useEffect(() => {
    return async () => {
      await audioRecorderPlayer.stopRecorder()
      audioRecorderPlayer.removeRecordBackListener()
      clearFallbackTimer()
    }
  }, [])

  return {
    recording,
    setRecording,
    recordTime,
    onVoiceRecorderStart,
    onVoiceRecorderStop,
  }
}
export default useVoiceRecord
